import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Package,
  CreditCard,
  Upload,
  X,
  CheckCircle2,
  AlertCircle,
  ArrowRight,
  Download,
  FileText,
} from "lucide-react";
import { useAuthStore } from "../../store/auth";
import { useProdutosStore } from "../../store/produtos";
import { api } from "../../api/greenclub";
const documentosPadrao = [
  {
    tipo: "comprovante_residencia",
    nome: "Comprovante de residência (PDF ou imagem)",
    obrigatorio: true,
  },
  {
    tipo: "rg_cpf",
    nome: "Documento com foto (RG/CPF) - frente e verso em um único arquivo",
    obrigatorio: true,
  },
  {
    tipo: "prescricao_medica",
    nome: "Prescrição médica válida",
    obrigatorio: true,
  },
];
const statusConfigs = {
  pendente: { label: "Em análise", classes: "bg-yellow-100 text-yellow-800" },
  processando: { label: "Processando", classes: "bg-blue-100 text-blue-800" },
  pagamento_aprovado: {
    label: "Pagamento aprovado",
    classes: "bg-emerald-100 text-emerald-700",
  },
  pagamento_rejeitado: {
    label: "Pagamento rejeitado",
    classes: "bg-red-100 text-red-700",
  },
  enviado: { label: "Enviado", classes: "bg-indigo-100 text-indigo-700" },
  entregue: { label: "Entregue", classes: "bg-emerald-100 text-emerald-700" },
  cancelado: { label: "Cancelado", classes: "bg-gray-100 text-gray-600" },
};
const formatCurrency = (value) =>
  value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};
const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      const base64 = result.split(",")[1];
      if (!base64) {
        reject(new Error("Não foi possível converter o arquivo."));
      } else {
        resolve(base64);
      }
    };
    reader.onerror = () => reject(new Error("Erro ao ler o arquivo."));
    reader.readAsDataURL(file);
  });
const DashboardPaciente = () => {
  const { user, logout } = useAuthStore();
  const { produtos, carregarProdutos } = useProdutosStore();
  const [activeTab, setActiveTab] = useState("catalogo");
  const [produtosLoading, setProdutosLoading] = useState(false);
  const [pedidos, setPedidos] = useState([]);
  const [pedidosLoading, setPedidosLoading] = useState(false);
  const [sucesso, setSucesso] = useState(null);
  const [erro, setErro] = useState(null);
  const [modalCheckout, setModalCheckout] = useState({ aberto: false });
  const [produtoSelecionado, setProdutoSelecionado] = useState(null);
  const [quantidade, setQuantidade] = useState(1);
  const [documentos, setDocumentos] = useState(documentosPadrao);
  const [observacoes, setObservacoes] = useState("");
  const [enviandoPedido, setEnviandoPedido] = useState(false);
  const [cupomCodigo, setCupomCodigo] = useState("");
  const [cupomAplicado, setCupomAplicado] = useState(null);
  const [cupomFeedback, setCupomFeedback] = useState(null);
  const [validandoCupom, setValidandoCupom] = useState(false);
  const [erroCheckout, setErroCheckout] = useState(null);
  const [modalDetalhes, setModalDetalhes] = useState({
    aberto: false,
    carregando: false,
  });
  const carregarPedidos = useCallback(async () => {
    setPedidosLoading(true);
    setErro(null);
    try {
      const response = await api.getPedidos();
      setPedidos(response.pedidos || []);
    } catch (error) {
      console.error("Erro ao carregar pedidos:", error);
      setErro(error?.message || "Não foi possível carregar seus pedidos.");
    } finally {
      setPedidosLoading(false);
    }
  }, []);
  useEffect(() => {
    setProdutosLoading(true);
    carregarProdutos()
      .catch((error) => {
        console.error("Erro ao carregar produtos:", error);
        setErro("Não foi possível carregar o catálogo no momento.");
      })
      .finally(() => setProdutosLoading(false));
    carregarPedidos();
  }, [carregarProdutos, carregarPedidos]);
  const resetCheckoutState = () => {
    setQuantidade(1);
    setDocumentos(documentosPadrao);
    setObservacoes("");
    setCupomCodigo("");
    setCupomAplicado(null);
    setCupomFeedback(null);
    setValidandoCupom(false);
    setEnviandoPedido(false);
    setErroCheckout(null);
  };
  const handleAplicarCupom = async () => {
    if (!cupomCodigo.trim()) {
      setCupomFeedback("Informe um código de cupom.");
      return;
    }
    setValidandoCupom(true);
    setCupomFeedback(null);
    try {
      const response = await api.validateCoupon(
        cupomCodigo.trim().toUpperCase(), // codigo
        produtoSelecionado?._id // produtoId
      );
      if (response.valid) {
        setCupomAplicado(response.cupom);
        setCupomCodigo(response.cupom.codigo);
        setCupomFeedback(
          `Cupom ${response.cupom.codigo} aplicado com sucesso!`
        );
      } else {
        setCupomAplicado(null);
        setCupomFeedback(response.message || "Cupom inválido ou expirado.");
      }
    } catch (error) {
      console.error("Erro ao validar cupom:", error);
      setCupomAplicado(null);
      setCupomFeedback(error?.message || "Não foi possível validar o cupom.");
    } finally {
      setValidandoCupom(false);
    }
  };
  const handleRemoverCupom = () => {
    setCupomAplicado(null);
    setCupomFeedback("Cupom removido.");
  };
  const calcularDescontoEstimado = () => {
    if (!produtoSelecionado || !cupomAplicado) return 0;
    const subtotal = produtoSelecionado.preco * (quantidade || 1);
    if (cupomAplicado.tipo === "percentual") {
      return (subtotal * (cupomAplicado.valor || 0)) / 100;
    }
    if (cupomAplicado.tipo === "valor_fixo") {
      return Math.min(cupomAplicado.valor || 0, subtotal);
    }
    return 0;
  };
  const handleAbrirCheckout = (produtoId) => {
    const produto = produtos.find((item) => item._id === produtoId);
    if (!produto) {
      setErro("Produto não encontrado.");
      return;
    }
    resetCheckoutState();
    setProdutoSelecionado(produto);
    console.log(
      "Abrindo checkout para o produto:",
      JSON.stringify(produto, null, 2)
    );
    setModalCheckout({ aberto: true, produtoId });
  };
  const handleFecharCheckout = () => {
    setModalCheckout({ aberto: false });
    resetCheckoutState();
  };
  const handleArquivoChange = async (index, fileList) => {
    if (!fileList || fileList.length === 0) {
      setDocumentos((prev) => {
        const updated = [...prev];
        updated[index] = {
          ...updated[index],
          arquivo: undefined,
          url: undefined,
          conteudo_base64: undefined,
          tipo_arquivo: undefined,
        };
        return updated;
      });
      return;
    }
    const file = fileList[0];
    if (file.size > 10 * 1024 * 1024) {
      setErro("Os arquivos devem ter no máximo 10MB.");
      return;
    }
    setDocumentos((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], arquivo: file, url: undefined }; // Limpa URL antiga
      return updated;
    });
  };
  const handleAdicionarDocumentoExtra = () => {
    setDocumentos((prev) => [
      ...prev,
      {
        tipo: `anexo_${Date.now()}`,
        nome: "Documento adicional",
        obrigatorio: false,
      },
    ]);
  };
  const handleRemoverDocumentoExtra = (index) => {
    const doc = documentos[index];
    if (doc.obrigatorio) return;
    setDocumentos((prev) => prev.filter((_, idx) => idx !== index));
  };
  const validarDocumentos = () => {
    const ausentes = documentos.filter(
      (doc) => doc.obrigatorio && !doc.arquivo
    );
    if (ausentes.length > 0) {
      setErroCheckout(
        "Por favor, anexe todos os documentos obrigatórios antes de finalizar a compra."
      );
      return false;
    }
    return true;
  };
  const handleFinalizarCompra = async () => {
    if (!produtoSelecionado) return;
    if (quantidade <= 0) {
      setErroCheckout("Informe uma quantidade válida.");
      return;
    }
    const quantidadeInteira = Math.max(1, Math.floor(quantidade));
    if (!validarDocumentos()) {
      return;
    }
    setEnviandoPedido(true);
    setErro(null);
    setErroCheckout(null);

    try {
      // 1. Fazer upload dos arquivos para o Cloudinary via nosso backend
      const documentosParaEnvio = [];
      for (const doc of documentos) {
        if (doc.arquivo) {
          let url = doc.url; // Reutiliza URL se já foi feito upload
          if (!url) {
            const formData = new FormData();
            formData.append("file", doc.arquivo);
            const uploadResponse = await api.uploadFile(formData);
            if (!uploadResponse.success || !uploadResponse.url) {
              // Interrompe o processo se o upload falhar
              throw new Error(
                `Falha no upload do arquivo: ${doc.arquivo.name}. Tente novamente.`
              );
            }
            url = uploadResponse.url;
          }
          documentosParaEnvio.push({
            tipo: doc.tipo,
            nome: doc.arquivo.name,
            url: url,
            tipo_arquivo: doc.arquivo.type,
          });
        }
      }

      // 2. Montar o payload do pedido com as URLs
      const descontoEstimado = calcularDescontoEstimado();

      const payload = {
        itens: [
          {
            produto_id: produtoSelecionado.id, // CORREÇÃO: O store renomeia _id para id
            quantidade: quantidadeInteira,
          },
        ],
        documentos: documentosParaEnvio,
        observacoes: observacoes || undefined,
        cupom_codigo: cupomAplicado?.codigo || undefined,
        desconto_estimado: descontoEstimado || undefined,
      };
      // 3. Criar o pedido
      console.log(
        "Enviando payload para criar pedido:",
        JSON.stringify(payload, null, 2)
      );
      await api.createPedido(payload);
      setSucesso(
        "Pedido enviado com sucesso! Nossa equipe analisará a documentação em breve."
      );
      handleFecharCheckout();
      setActiveTab("pedidos");
      carregarPedidos();
    } catch (error) {
      console.error("Erro ao finalizar compra:", error);
      setErro(
        error?.message || "Não foi possível concluir a compra. Tente novamente."
      );
    } finally {
      setEnviandoPedido(false);
    }
  };
  const abrirDetalhesPedido = async (pedidoId) => {
    setModalDetalhes({ aberto: true, carregando: true });
    setErro(null);
    try {
      const detalhe = await api.getPedidoById(pedidoId);
      setModalDetalhes({ aberto: true, carregando: false, dados: detalhe });
    } catch (error) {
      console.error("Erro ao carregar detalhes do pedido:", error);
      setModalDetalhes({ aberto: false, carregando: false });
      setErro(
        error?.message || "Não foi possível carregar os detalhes do pedido."
      );
    }
  };
  const fecharDetalhesPedido = () => {
    setModalDetalhes({ aberto: false, carregando: false, dados: undefined });
  };
  const baixarDocumento = (url, nome) => {
    if (!url) {
      setErro("A URL do documento é inválida.");
      return;
    }
    const link = document.createElement("a");
    link.href = url;
    link.target = "_blank"; // Abre em uma nova aba
    link.download = nome || "documento"; // Sugere um nome para o download
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50">
      <motion.header
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="bg-white shadow-sm border-b border-gray-200"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ">
          <div className="flex justify-between items-center h-16">
            <div>
              <h1 className="text-2xl font-bold text-emerald-800">GreenClub</h1>
              <p className="text-xs sm:text-sm text-gray-500">
                Bem-vindo, {user?.nome?.split(" ")[0] || "paciente"}!
              </p>
            </div>
            <button
              onClick={logout}
              className="bg-red-500 hover:bg-red-600 text-white px-3 py-2 sm:px-4 rounded-lg transition-colors text-sm"
            >
              Sair
            </button>
          </div>
        </div>
      </motion.header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm rounded-lg p-4 flex items-start gap-3"
        >
          <AlertCircle className="h-5 w-5 mt-1" />
          <div>
            <p className="font-semibold">Garantia de validade</p>
            <p>
              Produtos adquiridos com cupons possuem garantia de validade mínima
              de 3 meses para uso, desde que armazenados conforme as orientações
              da clínica responsável.
            </p>
          </div>
        </motion.div>
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="relative mb-6"
        >
          <div className="overflow-x-auto pb-2 -mb-2">
            <div className="flex space-x-1 bg-white p-1 rounded-lg shadow-sm w-max">
              {[
                { id: "catalogo", label: "Catálogo de Produtos", icon: Package },
                { id: "pedidos", label: "Histórico de Compras", icon: CreditCard },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 px-3 sm:px-4 py-2 rounded-md transition-colors text-sm whitespace-nowrap ${
                    activeTab === tab.id
                      ? "bg-emerald-100 text-emerald-700 font-semibold"
                      : "text-gray-600 hover:bg-gray-100 hover:text-gray-800"
                  }`}
                >
                  <tab.icon className="w-4 h-4" />
                  <span>{tab.label}</span>
                </button>
              ))}
            </div>
          </div>
        </motion.div>

        <div className="space-y-4">
          {sucesso && (
            <div className="flex items-center gap-2 bg-emerald-100 border border-emerald-200 text-emerald-700 px-4 py-3 rounded-lg">
              <CheckCircle2 className="w-5 h-5" />
              <span>{sucesso}</span>
            </div>
          )}

          {erro && (
            <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg">
              <AlertCircle className="w-5 h-5" />
              <span>{erro}</span>
            </div>
          )}
        </div>

        {activeTab === "catalogo" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="mt-6"
          >
            {produtosLoading ? (
              <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-10 text-center text-gray-500">
                Carregando catálogo...
              </div>
            ) : produtos.length === 0 ? (
              <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-10 text-center text-gray-500">
                Nenhum produto disponível no momento.
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {produtos.map((produto, index) => (
                  <motion.div
                    key={produto._id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.05 * index }}
                    className="bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-all"
                  >
                    <div className="aspect-w-16 aspect-h-9 bg-gradient-to-br from-emerald-100 to-teal-100 rounded-t-xl flex items-center justify-center">
                      <span className="text-4xl">🌿</span>
                    </div>
                    <div className="p-6 space-y-4">
                      <div>
                        <h3 className="font-semibold text-gray-900 text-lg">
                          {produto.nome}
                        </h3>
                        <p className="text-sm text-gray-600 mt-1 leading-relaxed">
                          {produto.descricao}
                        </p>
                      </div>

                      <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-4">
                        <p className="text-xs uppercase tracking-wide text-emerald-700 font-semibold mb-1">
                          Valor unitário
                        </p>
                        <p className="text-3xl font-bold text-emerald-600">
                          {formatCurrency(produto.preco)}
                        </p>
                        <p className="text-xs text-emerald-700 mt-2">
                          Dosagem: {produto.dosagem}
                        </p>
                        <p className="text-xs text-emerald-700">
                          Fabricante: {produto.fabricante}
                        </p>
                      </div>

                      <button
                        onClick={() => handleAbrirCheckout(produto._id)}
                        className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-medium py-2.5 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
                      >
                        <ArrowRight className="w-4 h-4" />
                        Comprar agora
                      </button>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </motion.div>
        )}

        {activeTab === "pedidos" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="mt-6"
          >
            {pedidosLoading ? (
              <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-10 text-center text-gray-500">
                Buscando seus pedidos...
              </div>
            ) : pedidos.length === 0 ? (
              <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-10 text-center text-gray-500">
                Você ainda não realizou compras. Explore o catálogo e faça seu
                primeiro pedido!
              </div>
            ) : (
              <div className="space-y-4">
                {pedidos.map((pedido) => {
                  const statusInfo =
                    statusConfigs[pedido.status] || statusConfigs.pendente;
                  return (
                    <div
                      key={pedido._id}
                      className="bg-white border border-gray-200 rounded-xl shadow-sm p-6" // Use _id for MongoDB
                    >
                      <div className="flex flex-wrap justify-between gap-4 items-start">
                        <div>
                          <div className="flex items-center gap-3">
                            <span className="text-sm text-gray-500">
                              Pedido #{pedido._id}
                            </span>
                            <span
                              className={`px-3 py-1 rounded-full text-xs font-semibold ${statusInfo.classes}`}
                            >
                              {statusInfo.label}
                            </span>
                          </div>
                          <p className="text-sm text-gray-500 mt-1">
                            Realizado em {formatDateTime(pedido.criado_em)}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs uppercase text-gray-500">
                            Valor total
                          </p>
                          <p className="text-xl font-semibold text-emerald-600">
                            {formatCurrency(pedido.total || 0)}
                          </p>
                        </div>
                      </div>

                      {pedido.documentos?.length > 0 && (
                        <div className="mt-4">
                          <p className="text-sm font-medium text-gray-700 mb-2">
                            Documentos anexados:
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {pedido.documentos.map((doc) => (
                              <button
                                key={doc.url} // Usar a URL como key, ou um ID único se houver
                                onClick={() =>
                                  baixarDocumento(doc.url, doc.nome)
                                }
                                className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-emerald-50 border border-emerald-100 text-emerald-700 rounded-lg hover:bg-emerald-100 transition-colors"
                              >
                                <FileText className="w-4 h-4" />
                                <span>{doc.arquivo_nome || doc.tipo}</span>
                                <Download className="w-4 h-4" />
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="mt-6 pt-4 border-t border-gray-100 flex flex-wrap gap-3 justify-between items-center">
                        <div className="text-sm text-gray-500">
                          Cupom utilizado: {pedido.cupom_codigo || "Nenhum"}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </motion.div>
        )}
      </div>

      <AnimatePresence>
        {modalCheckout.aberto && produtoSelecionado && (
          <motion.div
            className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-start md:items-center justify-center z-40 p-4 overflow-y-auto"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 20 }}
              className="relative bg-white rounded-2xl shadow-2xl max-w-3xl w-full my-8"
            >
              <button
                onClick={handleFecharCheckout}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>

              <div className="p-6 md:p-8 space-y-6 max-h-[90vh] md:max-h-none overflow-y-auto">
                <div>
                  <h2 className="text-2xl font-semibold text-gray-900">
                    Finalizar compra
                  </h2>
                  <p className="text-sm text-gray-500 mt-1">
                    Anexe os documentos necessários para concluir o pedido.
                  </p>
                </div>

                {erroCheckout && (
                  <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm">
                    <AlertCircle className="w-5 h-5 flex-shrink-0" />
                    <span>{erroCheckout}</span>
                  </div>
                )}

                <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-5">
                  <p className="text-xs uppercase text-emerald-700 font-semibold">
                    Produto selecionado
                  </p>
                  <div className="mt-2 flex flex-wrap gap-4 justify-between items-start">
                    <div>
                      <p className="text-lg font-semibold text-emerald-800">
                        {produtoSelecionado.nome}
                      </p>
                      <p className="text-sm text-emerald-700">
                        {produtoSelecionado.descricao}
                      </p>
                      <p className="text-xs text-emerald-700 mt-2">
                        Dosagem: {produtoSelecionado.dosagem}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-emerald-700">Valor unitário</p>
                      <p className="text-2xl font-bold text-emerald-600">
                        {formatCurrency(produtoSelecionado.preco)}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Quantidade desejada
                      </label>
                      <input
                        type="number"
                        min={1}
                        value={quantidade}
                        onChange={(e) =>
                          setQuantidade(parseInt(e.target.value, 10) || 1)
                        }
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Informe a quantidade que deseja adquirir deste produto.
                      </p>
                    </div>

                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
                      <p className="text-xs uppercase text-gray-500 font-semibold">
                        Resumo do pedido
                      </p>
                      <div className="space-y-2 text-sm text-gray-700">
                        <div className="flex justify-between">
                          <span>Produto</span>
                          <span>
                            {formatCurrency(produtoSelecionado.preco)}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span>Quantidade</span>
                          <span>{quantidade}</span>
                        </div>
                        {cupomAplicado && (
                          <div className="flex justify-between text-emerald-600">
                            <span>
                              Desconto (
                              {cupomAplicado.descricao || cupomAplicado.codigo})
                            </span>
                            <span>
                              - {formatCurrency(calcularDescontoEstimado())}
                            </span>
                          </div>
                        )}
                        <div className="border-t border-dashed border-gray-300 pt-2 flex justify-between font-semibold text-emerald-700">
                          <span>Total estimado</span>
                          <span>
                            {formatCurrency(
                              Math.max(
                                produtoSelecionado.preco * quantidade -
                                  calcularDescontoEstimado(),
                                0
                              )
                            )}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Cupom de desconto
                      </label>
                      <div className="flex flex-wrap gap-2">
                        <input
                          type="text"
                          value={cupomCodigo}
                          onChange={(e) =>
                            setCupomCodigo(e.target.value.toUpperCase())
                          }
                          placeholder="Informe o código"
                          className="flex-1 min-w-[180px] border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                          disabled={validandoCupom}
                        />
                        {cupomAplicado ? (
                          <button
                            onClick={handleRemoverCupom}
                            type="button"
                            className="px-4 py-2 text-sm font-semibold text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors"
                          >
                            Remover
                          </button>
                        ) : (
                          <button
                            onClick={handleAplicarCupom}
                            type="button"
                            className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
                            disabled={validandoCupom}
                          >
                            {validandoCupom ? "Validando..." : "Aplicar"}
                          </button>
                        )}
                      </div>
                      {cupomFeedback && (
                        <p
                          className={`text-xs ${
                            cupomAplicado ? "text-emerald-600" : "text-red-500"
                          }`}
                        >
                          {cupomFeedback}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Observações adicionais (opcional)
                      </label>
                      <textarea
                        value={observacoes}
                        onChange={(e) => setObservacoes(e.target.value)}
                        rows={3}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        placeholder="Inclua informações relevantes para nossa equipe clínica."
                      />
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-semibold text-gray-700">
                        Documentos obrigatórios
                      </h3>
                      <button
                        onClick={handleAdicionarDocumentoExtra}
                        className="text-emerald-600 text-xs font-medium hover:underline"
                      >
                        Adicionar documento extra
                      </button>
                    </div>

                    <div className="space-y-3 max-h-80 overflow-y-auto pr-2">
                      {documentos.map((doc, index) => (
                        <div
                          key={doc.tipo + "-" + index}
                          className="border border-gray-200 rounded-lg p-3"
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <p className="text-sm font-medium text-gray-800 flex items-center gap-2">
                                <Upload className="w-4 h-4 text-emerald-500" />
                                {doc.nome}
                                {doc.obrigatorio && (
                                  <span className="text-xs text-red-500 font-semibold">
                                    * obrigatório
                                  </span>
                                )}
                              </p>
                              {doc.arquivo ? (
                                <p className="text-xs text-gray-500 mt-1">
                                  {doc.arquivo.name} •{" "}
                                  {(doc.arquivo.size / (1024 * 1024)).toFixed(
                                    2
                                  )}{" "}
                                  MB
                                </p>
                              ) : (
                                <p className="text-xs text-gray-500 mt-1">
                                  Formatos aceitos: PDF, JPG, PNG, até 10MB
                                </p>
                              )}
                            </div>
                            {!doc.obrigatorio && (
                              <button
                                onClick={() =>
                                  handleRemoverDocumentoExtra(index)
                                }
                                className="text-xs text-red-500 hover:underline"
                              >
                                remover
                              </button>
                            )}
                          </div>

                          <label className="mt-3 w-full inline-flex items-center justify-center px-3 py-2 border border-dashed border-emerald-300 rounded-lg text-sm font-medium text-emerald-600 hover:bg-emerald-50 cursor-pointer transition-colors">
                            Selecionar arquivo
                            <input
                              type="file"
                              className="hidden"
                              accept=".pdf,.jpg,.jpeg,.png"
                              onChange={(event) =>
                                handleArquivoChange(index, event.target.files)
                              }
                            />
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap justify-end gap-3 pt-2">
                  <button
                    onClick={handleFecharCheckout}
                    className="px-4 py-2 text-sm font-medium text-gray-500 hover:text-gray-700"
                    disabled={enviandoPedido}
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleFinalizarCompra}
                    disabled={enviandoPedido}
                    className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-70 disabled:cursor-not-allowed text-white px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors flex items-center gap-2"
                  >
                    {enviandoPedido
                      ? "Enviando pedido..."
                      : "Enviar para análise"}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {modalDetalhes.aberto && (
          <motion.div
            className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-40 px-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 20 }}
              className="relative bg-white rounded-2xl shadow-2xl max-w-3xl w-full"
            >
              <button
                onClick={fecharDetalhesPedido}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>

              <div className="p-6 md:p-8 space-y-6">
                <h2 className="text-xl font-semibold text-gray-900">
                  Detalhes do pedido
                </h2>

                {modalDetalhes.carregando ? (
                  <div className="py-10 text-center text-gray-500">
                    Carregando informações do pedido...
                  </div>
                ) : modalDetalhes.dados ? (
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                        <p className="text-xs uppercase text-gray-500 font-semibold">
                          Identificação
                        </p>
                        <p className="text-sm text-gray-600 mt-2">
                          Pedido #{modalDetalhes.dados.pedido._id}
                        </p>
                        <p className="text-sm text-gray-600">
                          Criado em{" "}
                          {formatDateTime(modalDetalhes.dados.pedido.criado_em)}
                        </p>
                        <p className="text-sm text-gray-600">
                          Status:{" "}
                          {statusConfigs[modalDetalhes.dados.pedido.status]
                            ?.label || "Em análise"}
                        </p>
                      </div>

                      <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-4">
                        <p className="text-xs uppercase text-emerald-700 font-semibold">
                          Resumo financeiro
                        </p>
                        <div className="mt-2 space-y-1 text-sm text-emerald-700">
                          <div className="flex justify-between">
                            <span>Subtotal</span>
                            <span>
                              {formatCurrency(
                                modalDetalhes.dados.pedido.subtotal || 0
                              )}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span>Desconto</span>
                            <span>
                              {formatCurrency(
                                modalDetalhes.dados.pedido.desconto || 0
                              )}
                            </span>
                          </div>
                          <div className="border-t border-emerald-200 pt-2 flex justify-between font-semibold text-emerald-800">
                            <span>Total</span>
                            <span>
                              {formatCurrency(
                                modalDetalhes.dados.pedido.total || 0
                              )}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h3 className="text-sm font-semibold text-gray-700 mb-2">
                        Itens do pedido
                      </h3>
                      <div className="space-y-2">
                        {modalDetalhes.dados.itens.map((item) => (
                          <div
                            key={item.id || item.produto_id}
                            className="border border-gray-200 rounded-lg p-3 flex justify-between text-sm"
                          >
                            <div>
                              <p className="font-medium text-gray-800">
                                {item.produto_nome}
                              </p>
                              <p className="text-gray-500">
                                Quantidade: {item.quantidade}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-gray-500">Valor unitário</p>
                              <p className="font-semibold text-emerald-600">
                                {formatCurrency(item.preco_unitario || 0)}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {modalDetalhes.dados.documentos.length > 0 && (
                      <div>
                        <h3 className="text-sm font-semibold text-gray-700 mb-2">
                          Documentos enviados
                        </h3>
                        <div className="flex flex-wrap gap-2">
                          {modalDetalhes.dados.documentos.map((doc) => (
                            <button
                              key={doc.url}
                              onClick={() => baixarDocumento(doc.url, doc.nome)}
                              className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-emerald-50 border border-emerald-100 text-emerald-700 rounded-lg hover:bg-emerald-100 transition-colors"
                            >
                              <FileText className="w-4 h-4" />
                              <span>{doc.arquivo_nome || doc.tipo}</span>
                              <Download className="w-4 h-4" />
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="py-10 text-center text-gray-500">
                    Não foi possível carregar os detalhes do pedido.
                  </div>
                )}

                <div className="flex justify-end">
                  <button
                    onClick={fecharDetalhesPedido}
                    className="px-4 py-2 text-sm font-medium text-gray-500 hover:text-gray-700"
                  >
                    Fechar
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
export default DashboardPaciente;
